/**
 * Block parser for long-form markdown documents. PURE: no React, no CSS
 * import, no DOM. That is deliberate and load-bearing — it is what lets this
 * run under the repo's `tsx --test` runner. Every other component in
 * packages/ui imports a .module.css, which the test runner cannot load, which
 * is why none of them are unit-tested. All the logic worth testing (table
 * detection, heading depth, bullet folding, paragraph joining) lives here;
 * MarkdownDocument.tsx only maps these blocks onto elements.
 *
 * Supports what the versioned legal documents actually contain and nothing
 * more: ## headings, pipe tables, - bullets, --- rules, > quotes (the Site
 * Host Terms' "In short" summaries), paragraphs. Anything unsupported
 * degrades to paragraph text rather than vanishing.
 */

export type DocBlock =
  | { kind: 'heading'; depth: number; text: string }
  | { kind: 'para'; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'rule' }
  | { kind: 'quote'; text: string }
  | { kind: 'table'; header: string[]; rows: string[][] };

const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const BULLET_RE = /^[ \t]*[-*•][ \t]+(.+?)[ \t]*$/;
const RULE_RE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;
/** A blockquote line: up to three spaces, then '>' and an optional space. */
const QUOTE_RE = /^ {0,3}>\s?(.*)$/;
/** A table row: trimmed, starts and ends with a pipe. */
const TABLE_ROW_RE = /^\s*\|.*\|\s*$/;
/** The separator under a header row: pipes, dashes, optional alignment colons. */
const TABLE_SEP_RE = /^\s*\|[\s:|-]+\|\s*$/;

/** Split "| a | b |" into cells, dropping the leading/trailing empties. */
function splitRow(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
}

function isTableStart(line: string, next: string): boolean {
  // The separator is required. Without it a line containing pipes is prose.
  return TABLE_ROW_RE.test(line) && TABLE_SEP_RE.test(next);
}

export function parseDocument(text: string): DocBlock[] {
  const lines = text.split('\n');
  const out: DocBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';

    if (!line.trim()) { i += 1; continue; }

    if (RULE_RE.test(line)) { out.push({ kind: 'rule' }); i += 1; continue; }

    // Headings shift DOWN ONE level: the accept card already owns an <h2> for
    // the document title, so a document's `##` becomes h3 and sits under it.
    // `#` -> 2, `##` -> 3, floored at 6.
    const h = HEADING_RE.exec(line);
    if (h) {
      out.push({
        kind: 'heading',
        depth: Math.min(6, (h[1]?.length ?? 1) + 1),
        text: h[2] ?? '',
      });
      i += 1;
      continue;
    }

    if (isTableStart(line, lines[i + 1] ?? '')) {
      const header = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && TABLE_ROW_RE.test(lines[i] ?? '')) {
        rows.push(splitRow(lines[i] ?? ''));
        i += 1;
      }
      out.push({ kind: 'table', header, rows });
      continue;
    }

    // A quote: consecutive '>' lines, markers stripped, joined with a space
    // like a paragraph. The legal documents use it for a section's short
    // summary, which must read as a callout, not as text starting with '>'.
    if (QUOTE_RE.test(line)) {
      const buf: string[] = [];
      while (i < lines.length) {
        const m = QUOTE_RE.exec(lines[i] ?? '');
        if (!m) break;
        const t = (m[1] ?? '').trim();
        if (t) buf.push(t);
        i += 1;
      }
      if (buf.length) out.push({ kind: 'quote', text: buf.join(' ') });
      continue;
    }

    if (BULLET_RE.test(line)) {
      const items: string[] = [];
      while (i < lines.length) {
        const m = BULLET_RE.exec(lines[i] ?? '');
        if (!m) break;
        let itemText = m[1] ?? '';
        i += 1;
        // An indented, unmarked line continues the item above it. Both
        // documents hard-wrap their bullets, so without this every wrapped
        // bullet would break into a stray paragraph mid-list.
        while (
          i < lines.length
          && (lines[i] ?? '').trim()
          && /^[ \t]+\S/.test(lines[i] ?? '')
          && !BULLET_RE.test(lines[i] ?? '')
        ) {
          itemText += ` ${(lines[i] ?? '').trim()}`;
          i += 1;
        }
        items.push(itemText);
      }
      out.push({ kind: 'list', items });
      continue;
    }

    // Paragraph: run to the next blank line or block starter. Soft line
    // breaks are joined with a SPACE, because these documents are hard-wrapped
    // at ~95 columns and honouring those wraps would render a ragged column
    // on a phone.
    const buf: string[] = [];
    while (i < lines.length) {
      const l = lines[i] ?? '';
      if (!l.trim() || RULE_RE.test(l) || HEADING_RE.test(l) || BULLET_RE.test(l) || QUOTE_RE.test(l)) break;
      if (isTableStart(l, lines[i + 1] ?? '')) break;
      buf.push(l.trim());
      i += 1;
    }
    out.push({ kind: 'para', text: buf.join(' ') });
  }

  return out;
}
